import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../redux/hooks";
import { RootState } from "redux/store";
import { fetchIntegrationLookup } from "../services/integrationFetchLookup.service";
import { setIntegrationLookups, INTEGRATIONS_CACHE_TTL_MS } from "../redux/slices/integrations.slice";
import { useGetIntegrationConfig } from "../services/integrations.service";
import { Utils } from "utils";
import { getIntegrationConfig } from "config/integrations.registry";

export interface IntegrationData {
  type: string;
  title: string | null;
  icon: string | null;
  onClick: (() => void) | null;
  data: any;
}

interface Integration {
  name: string;
  config: Record<string, any>;
  lookups: any[];
}

export const useIntegrationLookups = () => {
  const dispatch = useAppDispatch();
  const fetchedCallerIDs = useRef<Set<string>>(new Set());
  const integrationsRef = useRef<Integration[]>([]);
  const integrations = useSelector((state: RootState) => state.integrations?.items ?? []);

  useEffect(() => {
    integrationsRef.current = integrations;
  }, [integrations]);

  return {
    fetchIntegrationLookups: (numbers: string[]) => {
      if (!numbers.length) return;

      numbers.forEach((num: string) => {
        if (!fetchedCallerIDs.current.has(num)) {
          fetchedCallerIDs.current.add(num);
        }
      });

      if (integrationsRef.current?.length > 0) {
        integrationsRef.current.forEach((item: Integration) => {
          switch (item.name) {
            case "hhaexchange":
              const now = Date.now();
              const expiredOrNewNumbers = numbers?.filter((numE164: string) =>
                !item?.lookups?.find((lookup: any) => {
                  const matches = [lookup.HomePhone, lookup.Phone2, lookup.Phone3].includes(numE164);
                  if (!matches) return false;
                  const ts = Number(lookup._cachedAt) || 0;
                  const isFresh = ts && now - ts <= INTEGRATIONS_CACHE_TTL_MS;
                  return isFresh
                })
              );
              
              if(!expiredOrNewNumbers.length) return

              fetchIntegrationLookup("hhaexchange", expiredOrNewNumbers).then((lookupData) => {
                const formattedLookupData = lookupData.map((lookup: any) => ({
                  ...lookup,
                  HomePhone: lookup?.HomePhone ? Utils.phoneNumberFormatE164(lookup.HomePhone) : lookup?.HomePhone,
                  Phone2: lookup?.Phone2 ? Utils.phoneNumberFormatE164(lookup.Phone2) : lookup?.Phone2,
                  Phone3: lookup?.Phone3 ? Utils.phoneNumberFormatE164(lookup.Phone3) : lookup?.Phone3,
                }));

                dispatch(setIntegrationLookups({
                  name: "hhaexchange",
                  lookups: formattedLookupData
                }));
              }).catch((error) => {
                console.error("Failed to fetch HHA Exchange lookup:", error);
              });
              break;
            default:
              break;
          }
        });
      }
    },
  };
};

export const useGetIntegrationData = () => {
  const integrations = useSelector((state: RootState) => state.integrations.items);

  const getIntegrationsData = useCallback((num: string) => {
    if (!num) return [];

    const number = Utils.phoneNumberFormatE164(num);
    const data: any[] = [];

    if (integrations?.length > 0) {
      integrations.forEach((item: Integration) => {
        switch (item.name) {
          case "hhaexchange":
            const result = item.lookups.find((lookup: any) =>
              Utils.phoneNumberFormatE164(lookup.HomePhone) == number || Utils.phoneNumberFormatE164(lookup.Phone2) == number || Utils.phoneNumberFormatE164(lookup.Phone3) == number
            );
            if (result && item.config?.ent_id) {
              const integrationConfigData = getIntegrationConfig(item.name);
              let url = "";
              data.push({
                type: "hhaexchange",
                icon: integrationConfigData?.icon,
                title: integrationConfigData?.title,
                onClick: () => {
                  if (result.RecordType && result.RecordType === "Caregiver") {
                    url = `https://app.hhaexchange.com/${item.config.ent_id}/Aide/Aide.aspx?AideId=${result.ID}`;
                  } else if (result.RecordType && result.RecordType === "Patient") {
                    url = `https://app.hhaexchange.com/${item.config.ent_id}/Patient/InternalPatientInfo.aspx?PatientId=${result.ID}`;
                  }
                  window.open(url, "_blank");
                },
                data: result,
              });
            }
            break;
          default:
            break;
        }
      });
    }

    return data;
  }, [integrations]);

  return {
    getIntegrationsData,
  };
};

export const useIntegrationsConfig = () => {
  const dispatch = useAppDispatch();
  const integrations = useSelector((state: RootState) => state.integrations.items);
  const integrationsRef = useRef<Integration[]>([]);
  const configFetchedRef = useRef<Set<string>>(new Set());

  const hhaExchangeConfigHook = useGetIntegrationConfig("hhaexchange");


  const getConfigHook = useCallback((integrationName: string) => {
    switch (integrationName) {
      case "hhaexchange":
        return hhaExchangeConfigHook;
      default:
        return null;
    }
  }, [hhaExchangeConfigHook]);

  const checkAndFetchConfig = useCallback((item: Integration) => {
    const hasConfig = item.config && Object.keys(item.config).length > 0;
    const configKey = `${item.name}-config`;

    if (!hasConfig && !configFetchedRef.current.has(configKey)) {
      switch (item.name) {
        case "hhaexchange":
          if (!item.config?.ent_id) {
            const configHook = getConfigHook(item.name);
            if (configHook) {
              configFetchedRef.current.add(configKey);
              configHook.refetch();
            }
          }
          break;
        default:
          break;
      }
    }
  }, [getConfigHook]);

  useEffect(() => {
    integrationsRef.current = integrations;

    if (integrations?.length > 0) {
      integrations.forEach((item: Integration) => {
        checkAndFetchConfig(item);
      });
    }
  }, [integrations, checkAndFetchConfig]);

  useEffect(() => {
    const currentIntegrationNames = integrations.map(i => i.name).sort().join(',');
    const cachedNames = Array.from(configFetchedRef.current).map(key => key.replace('-config', '')).sort().join(',');

    if (currentIntegrationNames !== cachedNames) {
      configFetchedRef.current.clear();
    }
  }, [integrations]);

  return useMemo(() => ({
    integrations,
  }), [integrations]);
};
